import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Check, CheckCircle2, Monitor, Plus, TrendingDown } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";

const ASSET_STATUS = { active: "Active", assigned: "Assigned", under_repair: "Under Repair", disposed: "Disposed", sold: "Sold", lost: "Lost" };
const CATEGORIES = ["furniture", "it_equipment", "machinery", "vehicle", "tool", "electrical", "other"];
const EMPTY_ASSET = { name: "", category: "other", purchase_date: "", purchase_value: 0, supplier: "",
  location: "", employee_id: "", serial_no: "", warranty_expiry: "", depreciation_rate: 0, notes: "" };
const EMPTY_EQP = { name: "", category: "other", serial_no: "", purchase_date: "", purchase_value: 0,
  location: "", employee_id: "", condition: "good", next_maintenance: "", remarks: "" };
const EMPTY_CONV = { employee_id: "", date: new Date().toISOString().slice(0, 10), purpose: "",
  from_location: "", to_location: "", vehicle: "", distance_km: 0, amount: "", party_id: "", remarks: "" };

function useMasters(open) {
  const [employees, setEmployees] = useState([]);
  const [parties, setParties] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/employees?limit=300").then((r) => setEmployees(r.data.items)).catch(() => setEmployees([]));
    api.get("/parties?limit=200").then((r) => setParties(r.data.items)).catch(() => {});
  }, [open]);
  return { employees, parties };
}

function AssetsTab() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState({ open: false, asset: null });
  const [form, setForm] = useState(EMPTY_ASSET);
  const [busy, setBusy] = useState(false);
  const { employees } = useMasters(dialog.open);

  const load = () => api.get("/assets?limit=300").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line

  useEffect(() => { setForm(dialog.asset ? { ...EMPTY_ASSET, ...dialog.asset } : EMPTY_ASSET); }, [dialog]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Asset name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, purchase_value: +form.purchase_value || 0, depreciation_rate: +form.depreciation_rate || 0 };
      if (dialog.asset) await api.put(`/assets/${dialog.asset.id}`, payload);
      else await api.post("/assets", payload);
      toast.success("Asset saved");
      setDialog({ open: false, asset: null }); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setStatus = async (a, status) => {
    try {
      await api.post(`/assets/${a.id}/status`, { status });
      toast.success(`${a.asset_code} → ${ASSET_STATUS[status]}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {data && <p className="text-sm text-slate-500">Current value of register: <Money value={data.total_value} className="font-bold" data-testid="assets-total-value" /></p>}
        <Button data-testid="new-asset-btn" onClick={() => setDialog({ open: true, asset: null })}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Asset</Button>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="assets-empty" title="No fixed assets" body="Register machinery, vehicles and equipment with depreciation." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="assets-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Code</th><th className="p-3">Asset</th><th className="p-3">Category</th>
                <th className="p-3">Purchased</th><th className="p-3 text-right">Purchase Value</th>
                <th className="p-3 text-right">Current Value</th><th className="p-3">Assigned To</th>
                <th className="p-3">Status</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} data-testid={`asset-row-${a.asset_code}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs text-slate-500">{a.asset_code}</td>
                    <td className="p-3 font-medium">{a.name}
                      {a.depreciation_rate > 0 && <span className="ml-1.5 text-[10px] text-slate-400 flex items-center gap-0.5 inline-flex"><TrendingDown className="h-3 w-3" />{a.depreciation_rate}%/yr</span>}
                    </td>
                    <td className="p-3 capitalize text-slate-500">{a.category.replace(/_/g, " ")}</td>
                    <td className="p-3">{fmtDate(a.purchase_date)}</td>
                    <td className="p-3 text-right"><Money value={a.purchase_value} /></td>
                    <td className="p-3 text-right"><Money value={a.current_value} className="font-semibold" /></td>
                    <td className="p-3 text-slate-500">{a.employee_name || "—"}</td>
                    <td className="p-3"><StatusBadge status={ASSET_STATUS[a.status]} /></td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" data-testid={`asset-edit-${a.asset_code}`} onClick={() => setDialog({ open: true, asset: a })}>Edit</Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" data-testid={`asset-status-${a.asset_code}`}>Status</Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900">
                            {Object.entries(ASSET_STATUS).map(([k, v]) => (
                              <DropdownMenuItem key={k} onClick={() => setStatus(a, k)} className="cursor-pointer">{v}</DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog.open} onOpenChange={() => setDialog({ open: false, asset: null })}>
        <DialogContent aria-describedby={undefined} className="max-w-xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="asset-dialog">
          <DialogHeader><DialogTitle className="font-display">{dialog.asset ? "Edit Asset" : "New Fixed Asset"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Asset Name *</Label><Input data-testid="asset-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="asset-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Serial No</Label><Input className="mt-1.5 font-mono" value={form.serial_no} onChange={(e) => set("serial_no", e.target.value)} /></div>
            <div><Label>Purchase Date</Label><Input type="date" className="mt-1.5" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} /></div>
            <div><Label>Purchase Value (₹)</Label><Input data-testid="asset-value" type="number" min="0" className="mt-1.5" value={form.purchase_value} onChange={(e) => set("purchase_value", e.target.value)} /></div>
            <div><Label>Depreciation %/yr</Label><Input type="number" min="0" max="100" className="mt-1.5" value={form.depreciation_rate} onChange={(e) => set("depreciation_rate", e.target.value)} /></div>
            <div><Label>Warranty Expiry</Label><Input type="date" className="mt-1.5" value={form.warranty_expiry} onChange={(e) => set("warranty_expiry", e.target.value)} /></div>
            <div><Label>Supplier</Label><Input className="mt-1.5" value={form.supplier} onChange={(e) => set("supplier", e.target.value)} /></div>
            <div><Label>Location</Label><Input className="mt-1.5" value={form.location} onChange={(e) => set("location", e.target.value)} /></div>
            <div className="col-span-2"><Label>Assigned Employee</Label>
              <Select value={form.employee_id || "none"} onValueChange={(v) => set("employee_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="asset-employee" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not assigned —</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, asset: null })}>Cancel</Button>
            <Button data-testid="asset-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Save Asset"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EquipmentTab() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_EQP);
  const [busy, setBusy] = useState(false);
  const { employees } = useMasters(dialog);

  const load = () => api.get("/equipment").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Equipment name required"); return; }
    setBusy(true);
    try {
      await api.post("/equipment", { ...form, purchase_value: +form.purchase_value || 0 });
      toast.success("Equipment added");
      setDialog(false); setForm(EMPTY_EQP); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button data-testid="new-equipment-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> Add Equipment</Button>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="equipment-empty" title="No office equipment" body="Track laptops, printers and tools with assignment and maintenance dates." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="equipment-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Code</th><th className="p-3">Equipment</th><th className="p-3">Serial</th>
                <th className="p-3">Location</th><th className="p-3">Assigned To</th><th className="p-3">Condition</th>
                <th className="p-3">Next Maintenance</th></tr></thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.id} data-testid={`equipment-row-${e.equipment_code}`} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="p-3 font-mono text-xs text-slate-500">{e.equipment_code}</td>
                    <td className="p-3 font-medium">{e.name}</td>
                    <td className="p-3 font-mono text-xs text-slate-500">{e.serial_no || "—"}</td>
                    <td className="p-3 text-slate-500">{e.location || "—"}</td>
                    <td className="p-3 text-slate-500">{e.employee_name || "—"}</td>
                    <td className="p-3"><Badge variant="secondary" className="capitalize text-[10px]">{e.condition.replace(/_/g, " ")}</Badge></td>
                    <td className="p-3">{fmtDate(e.next_maintenance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-lg bg-white dark:bg-slate-900" data-testid="equipment-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Monitor className="h-5 w-5 text-slate-400" /> Add Office Equipment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="equipment-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="HP Laptop 15s" /></div>
            <div><Label>Serial No</Label><Input className="mt-1.5 font-mono" value={form.serial_no} onChange={(e) => set("serial_no", e.target.value)} /></div>
            <div><Label>Condition</Label>
              <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["new", "good", "fair", "needs_repair", "damaged"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input className="mt-1.5" value={form.location} onChange={(e) => set("location", e.target.value)} /></div>
            <div><Label>Next Maintenance</Label><Input type="date" className="mt-1.5" value={form.next_maintenance} onChange={(e) => set("next_maintenance", e.target.value)} /></div>
            <div className="col-span-2"><Label>Assigned Employee</Label>
              <Select value={form.employee_id || "none"} onValueChange={(v) => set("employee_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not assigned —</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="equipment-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Add Equipment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CONV_NEXT = { submitted: ["approved", "rejected"], approved: ["reimbursed"] };

function ConveyanceTab() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_CONV);
  const [busy, setBusy] = useState(false);
  const { employees, parties } = useMasters(dialog);

  const load = () => api.get("/conveyance?limit=300").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.employee_id || !form.purpose.trim() || !(+form.amount > 0)) { toast.error("Employee, purpose and amount are required"); return; }
    setBusy(true);
    try {
      await api.post("/conveyance", { ...form, amount: +form.amount, distance_km: +form.distance_km || 0, party_id: form.party_id || "" });
      toast.success("Conveyance submitted");
      setDialog(false); setForm(EMPTY_CONV); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const advance = async (c, status) => {
    try {
      await api.post(`/conveyance/${c.id}/status`, { status });
      toast.success(`${c.doc_no} → ${status}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {data && <p className="text-sm text-slate-500">Total claimed: <Money value={data.total} className="font-bold" data-testid="conveyance-total" /></p>}
        <Button data-testid="new-conveyance-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Entry</Button>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="conveyance-empty" title="No conveyance entries" body="Log staff travel and material delivery runs for reimbursement." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="conveyance-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">No</th><th className="p-3">Date</th><th className="p-3">Employee</th>
                <th className="p-3">Purpose</th><th className="p-3">Route</th><th className="p-3 text-right">Amount</th>
                <th className="p-3">Status</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} data-testid={`conveyance-row-${c.doc_no}`} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="p-3 font-mono text-xs">{c.doc_no}</td>
                    <td className="p-3">{fmtDate(c.date)}</td>
                    <td className="p-3">{c.employee_name}</td>
                    <td className="p-3 max-w-[200px] truncate">{c.purpose}{c.party_name ? <span className="text-xs text-slate-400"> · {c.party_name}</span> : null}</td>
                    <td className="p-3 text-xs text-slate-500">{[c.from_location, c.to_location].filter(Boolean).join(" → ") || "—"}</td>
                    <td className="p-3 text-right"><Money value={c.amount} className="font-semibold" /></td>
                    <td className="p-3"><StatusBadge status={c.status.charAt(0).toUpperCase() + c.status.slice(1)} /></td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        {(CONV_NEXT[c.status] || []).map((s) => (
                          <Button key={s} size="sm" variant={s === "rejected" ? "ghost" : "outline"} className={`h-7 text-xs ${s === "rejected" ? "text-red-500" : ""}`}
                            data-testid={`conveyance-${s}-${c.doc_no}`} onClick={() => advance(c, s)}>
                            {s === "approved" ? <Check className="mr-1 h-3 w-3" /> : s === "reimbursed" ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <Ban className="h-3 w-3" />}
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-lg bg-white dark:bg-slate-900" data-testid="conveyance-dialog">
          <DialogHeader><DialogTitle className="font-display">New Conveyance Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => set("employee_id", v)}>
                <SelectTrigger data-testid="conveyance-employee" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" className="mt-1.5" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div className="col-span-2"><Label>Purpose *</Label><Input data-testid="conveyance-purpose" className="mt-1.5" value={form.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="Material delivery to site / client visit" /></div>
            <div><Label>From</Label><Input className="mt-1.5" value={form.from_location} onChange={(e) => set("from_location", e.target.value)} /></div>
            <div><Label>To</Label><Input className="mt-1.5" value={form.to_location} onChange={(e) => set("to_location", e.target.value)} /></div>
            <div><Label>Client/Supplier (optional)</Label>
              <Select value={form.party_id || "none"} onValueChange={(v) => set("party_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Amount (₹) *</Label><Input data-testid="conveyance-amount" type="number" min="0" className="mt-1.5" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="conveyance-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Submit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Assets() {
  return (
    <div data-testid="assets-page">
      <PageHeader title="Assets & Conveyance" testId="assets-title" sub="Fixed assets, office equipment and staff travel" />
      <Tabs defaultValue="assets">
        <TabsList className="mb-4">
          <TabsTrigger value="assets" data-testid="tab-fixed-assets">Fixed Assets</TabsTrigger>
          <TabsTrigger value="equipment" data-testid="tab-equipment">Office Equipment</TabsTrigger>
          <TabsTrigger value="conveyance" data-testid="tab-conveyance">Staff Conveyance</TabsTrigger>
        </TabsList>
        <TabsContent value="assets"><AssetsTab /></TabsContent>
        <TabsContent value="equipment"><EquipmentTab /></TabsContent>
        <TabsContent value="conveyance"><ConveyanceTab /></TabsContent>
      </Tabs>
    </div>
  );
}
